/*
 * Free samples (DSBLGS-34)
 *
 * - <free-samples-opener>: opens the modal with the current #FreeSamplesTemplate content.
 * - <free-samples-modal>: checkbox selection and "Add to cart" of the chosen samples.
 * - <free-samples-cleanup>: removes sample lines whose parent line is no longer in the cart.
 * - <free-samples-checkout-guard>: blocks checkout when the cart contains only free samples.
 *
 * Depends on global.js (fetchConfig, trapFocus, removeTrapFocus, routes) and cart.js (CartItems).
 */

if (!customElements.get('free-samples-opener')) {
  customElements.define('free-samples-opener', class FreeSamplesOpener extends HTMLElement {
    constructor() {
      super();
      const button = this.querySelector('button');
      if (!button) return;

      button.addEventListener('click', () => {
        const modal = document.querySelector(this.dataset.modal);
        const template = document.querySelector(this.dataset.template);
        if (modal && template) modal.open(template, button);
      });
    }
  });
}

if (!customElements.get('free-samples-modal')) {
  customElements.define('free-samples-modal', class FreeSamplesModal extends HTMLElement {
    constructor() {
      super();
      this.body = this.querySelector('[data-free-samples-body]');
      this.submitButton = this.querySelector('[data-free-samples-submit]');
      this.errorElement = this.querySelector('[data-free-samples-error]');
      this.loading = false;

      this.querySelectorAll('[data-free-samples-close]').forEach((button) => {
        button.addEventListener('click', () => this.hide());
      });
      this.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
      this.body.addEventListener('change', this.onChange.bind(this));
      this.submitButton.addEventListener('click', this.onSubmit.bind(this));
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    open(template, opener) {
      this.openedBy = opener;
      this.body.innerHTML = '';
      this.body.appendChild(template.content.cloneNode(true));
      this.body.scrollTop = 0;
      this.showError('');
      this.updateState();

      document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      const closeButton = this.querySelector('[data-free-samples-close]');
      if (typeof trapFocus === 'function') trapFocus(this, closeButton);
      // Same as the theme's ModalDialog: focus again once the open transition has started.
      setTimeout(() => { if (this.hasAttribute('open') && closeButton) closeButton.focus(); }, 100);
    }

    hide() {
      if (!this.hasAttribute('open') || this.loading) return;
      this.removeAttribute('open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.getFocusTargetAfterClose());
      setTimeout(() => {
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }, 200);
    }

    // The opener may have been replaced by a cart re-render; fall back to a live element.
    getFocusTargetAfterClose() {
      if (this.openedBy && this.openedBy.isConnected) return this.openedBy;
      const bar = document.getElementById('free-samples-bar');
      return (bar && bar.querySelector('button'))
        || document.querySelector('.cart-item--free-sample .cart-item__heading')
        || document.querySelector('#main-cart-items .cart-item__heading')
        || null;
    }

    // Lens strength copied from the parent line (see free-samples-option.liquid).
    inheritedProperties(input) {
      if (!input.dataset.parentProperties) return {};
      try {
        const parsed = JSON.parse(input.dataset.parentProperties);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        return {};
      }
    }

    getSelectedInputs() {
      return Array.from(this.body.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)'));
    }

    onChange(event) {
      const input = event.target;
      if (!input || input.type !== 'checkbox') return;
      const option = input.closest('.free-samples-option');
      if (option) option.classList.toggle('is-selected', input.checked);
      this.updateState();
    }

    updateState() {
      const wasDisabled = this.submitButton.disabled;
      this.submitButton.disabled = this.loading || this.getSelectedInputs().length === 0;

      // The focus trap snapshots the focusable elements, so re-arm it when the submit button toggles.
      if (wasDisabled !== this.submitButton.disabled && this.hasAttribute('open') && typeof trapFocus === 'function') {
        const active = document.activeElement;
        trapFocus(this, active && this.contains(active) ? active : this.querySelector('[data-free-samples-close]'));
      }
    }

    setLoading(loading) {
      this.loading = loading;
      this.classList.toggle('is-loading', loading);
      this.submitButton.querySelector('.loading-overlay').classList.toggle('hidden', !loading);
      this.updateState();
    }

    showError(message) {
      this.errorElement.textContent = message || '';
    }

    onSubmit() {
      const inputs = this.getSelectedInputs();
      if (!inputs.length || this.loading) return;

      const cartItems = document.querySelector('cart-items');
      const sections = cartItems ? cartItems.getSectionsToRender().map((section) => section.section) : [];
      const items = inputs.map((input) => ({
        id: parseInt(input.value, 10),
        quantity: 1,
        properties: { ...this.inheritedProperties(input), _free_sample_parent: input.dataset.parentKey }
      }));

      this.showError('');
      this.setLoading(true);

      fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) {
            this.showError(state.description || state.message || this.dataset.errorText);
            return;
          }
          if (cartItems && state.sections) cartItems.renderSections(state);
          document.body.dispatchEvent(new CustomEvent('freeSamplesAdded', { detail: state }));
          this.setLoading(false);
          this.hide();
        })
        .catch(() => this.showError(this.dataset.errorText))
        .finally(() => this.setLoading(false));
    }
  });
}

if (!customElements.get('free-samples-cleanup')) {
  customElements.define('free-samples-cleanup', class FreeSamplesCleanup extends HTMLElement {
    connectedCallback() {
      const keys = (this.dataset.keys || '').split(',').filter(Boolean);
      const constructor = this.constructor;
      constructor.attempts = constructor.attempts || 0;
      if (!keys.length || constructor.pending || constructor.attempts >= 2) return;

      constructor.pending = true;
      constructor.attempts += 1;

      const cartItems = this.closest('cart-items') || document.querySelector('cart-items');
      const sections = cartItems ? cartItems.getSectionsToRender().map((section) => section.section) : [];
      const updates = {};
      keys.forEach((key) => { updates[key] = 0; });

      fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ updates, sections, sections_url: window.location.pathname })
      })
        .then((response) => response.json())
        .then((state) => {
          if (cartItems && state.sections) cartItems.renderSections(state);
          if (!state.status) constructor.attempts = 0;
        })
        .catch((error) => console.error(error))
        .finally(() => { constructor.pending = false; });
    }
  });
}

if (!customElements.get('free-samples-checkout-guard')) {
  customElements.define('free-samples-checkout-guard', class FreeSamplesCheckoutGuard extends HTMLElement {
    connectedCallback() {
      this.applyState();
      FreeSamplesCheckoutGuard.install();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    get samplesOnly() {
      return this.dataset.samplesOnly === 'true';
    }

    get checkoutButton() {
      return document.querySelector(this.dataset.checkout || '#checkout');
    }

    applyState() {
      const button = this.checkoutButton;
      if (!button) return;
      button.disabled = this.samplesOnly;
      button.classList.toggle('free-samples-checkout-blocked', this.samplesOnly);

      // Third-party apps (e.g. order limit apps) toggle the same button; keep it blocked while samples-only.
      if (this.observer) this.observer.disconnect();
      if (this.samplesOnly && typeof MutationObserver === 'function') {
        this.observer = new MutationObserver(() => {
          if (this.isConnected && this.samplesOnly && !button.disabled) button.disabled = true;
        });
        this.observer.observe(button, { attributes: true, attributeFilter: ['disabled'] });
      }
    }

    // Guard element governing the given checkout button, if any.
    static guardFor(button) {
      return Array.from(document.querySelectorAll('free-samples-checkout-guard'))
        .find((guard) => guard.checkoutButton === button) || null;
    }

    static isBlocked(button) {
      const guard = button ? FreeSamplesCheckoutGuard.guardFor(button) : null;
      return !!guard && guard.samplesOnly;
    }

    static block(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const note = document.querySelector('.free-samples-checkout-note');
      if (note && note.scrollIntoView) note.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    // Document-level guards so checkout stays blocked even if another script re-enables the button.
    static install() {
      if (FreeSamplesCheckoutGuard.installed) return;
      FreeSamplesCheckoutGuard.installed = true;

      document.addEventListener('click', (event) => {
        const button = event.target.closest ? event.target.closest('[name="checkout"]') : null;
        if (button && FreeSamplesCheckoutGuard.isBlocked(button)) FreeSamplesCheckoutGuard.block(event);
      }, true);

      document.addEventListener('submit', (event) => {
        const submitter = event.submitter;
        if (submitter && submitter.name === 'checkout' && FreeSamplesCheckoutGuard.isBlocked(submitter)) {
          FreeSamplesCheckoutGuard.block(event);
        }
      }, true);
    }
  });
}
